import { ReactNode, useMemo } from 'react';

// material-ui
import { CssBaseline, GlobalStyles, StyledEngineProvider } from '@mui/material';
import { createTheme, ThemeOptions, ThemeProvider, Theme, TypographyVariantsOptions } from '@mui/material/styles';

// project import
import useConfig from 'hooks/useConfig';
import Palette from './palette';
import Typography from './typography';
import CustomShadows from './shadows';
import componentsOverride from './overrides';
import { cssVariables } from './colors';

// types
import { CustomShadowProps } from 'types/theme';
import { ThemeMode } from 'types/config';

// types
type ThemeCustomizationProps = {
  children: ReactNode;
};

// ==============================|| DEFAULT THEME - MAIN  ||============================== //

export default function ThemeCustomization({ children }: ThemeCustomizationProps) {
  const { themeDirection, mode, presetColor, fontFamily } = useConfig();

  const theme: Theme = useMemo<Theme>(() => Palette(mode, presetColor), [mode, presetColor]);

  const themeTypography: TypographyVariantsOptions = useMemo<TypographyVariantsOptions>(
    () => Typography(mode, fontFamily, theme),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mode, fontFamily]
  );
  const themeCustomShadows: CustomShadowProps = useMemo<CustomShadowProps>(() => CustomShadows(theme), [theme]);

  const themeOptions: ThemeOptions = useMemo(
    () => ({
      breakpoints: {
        values: {
          xs: 0,
          sm: 768,
          md: 1024,
          lg: 1266,
          xl: 1440
        }
      },
      direction: themeDirection,
      mixins: {
        toolbar: {
          minHeight: 60,
          paddingTop: 8,
          paddingBottom: 8
        }
      },
      palette: theme.palette,
      customShadows: themeCustomShadows,
      typography: themeTypography
    }),
    [themeDirection, theme, themeTypography, themeCustomShadows]
  );

  const themes: Theme = createTheme(themeOptions);
  themes.components = componentsOverride(themes);

  const isDark = mode === ThemeMode.DARK;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={themes}>
        <CssBaseline />
        <GlobalStyles
          styles={{
            ':root': {
              colorScheme: mode,
              // V10 palette — see themes/colors.ts and docs/design-system/COLORS.md
              ...cssVariables(isDark)
            },
            body: {
              backgroundColor: 'var(--nv-bg)',
              color: 'var(--nv-text-primary)'
            },
            'input, textarea, select': {
              colorScheme: mode
            },
            '.apexcharts-text, .apexcharts-title-text, .apexcharts-legend-text': {
              fill: 'var(--nv-text-secondary) !important',
              color: 'var(--nv-text-secondary) !important'
            },
            '.apexcharts-gridline, .apexcharts-xaxis-tick, .apexcharts-yaxis-tick': {
              stroke: 'var(--nv-divider) !important'
            },
            '.bg-white, .bg-gray-50, .bg-slate-50': {
              backgroundColor: 'var(--nv-surface) !important'
            },
            '.bg-gray-100, .bg-slate-100': {
              backgroundColor: 'var(--nv-surface-soft) !important'
            },
            '.border-gray-100, .border-gray-200, .border-slate-100, .border-slate-200': {
              borderColor: 'var(--nv-border-strong) !important'
            },
            '.text-gray-900, .text-slate-900, .text-black': {
              color: 'var(--nv-text-primary) !important'
            },
            '.text-gray-800, .text-gray-700, .text-slate-800, .text-slate-700': {
              color: 'var(--nv-text-secondary) !important'
            },
            '.text-gray-600, .text-gray-500, .text-slate-600, .text-slate-500': {
              color: 'var(--nv-text-muted) !important'
            },
            '.hover\\:bg-gray-50:hover, .hover\\:bg-slate-50:hover': {
              backgroundColor: 'var(--nv-surface-hover) !important'
            },
            '.MuiDialog-paper, .MuiPopover-paper, .MuiMenu-paper, .MuiDrawer-paper': {
              backgroundImage: 'none',
              backgroundColor: 'var(--nv-surface)',
              color: 'var(--nv-text-primary)'
            },
            '.MuiTableCell-root': {
              borderColor: 'var(--nv-divider)'
            },
            '.MuiTableRow-root:hover': {
              backgroundColor: isDark ? 'var(--nv-bg-hover)' : undefined
            },
            '.MuiOutlinedInput-root, .MuiInputBase-root': {
              color: 'var(--nv-text-primary)'
            },
            '.MuiInputLabel-root, .MuiFormHelperText-root': {
              color: 'var(--nv-text-muted)'
            },
            '.MuiOutlinedInput-notchedOutline': {
              borderColor: 'var(--nv-border-input)'
            },
            // focus ring: black in light, yellow in dark (COLORS.md · States)
            ':focus-visible': {
              outlineColor: 'var(--nv-focus-ring)'
            },
            '::selection': {
              backgroundColor: 'var(--nv-brand-soft)',
              color: 'var(--nv-text-primary)'
            },
            '.simplebar-scrollbar:before': {
              backgroundColor: isDark ? 'rgba(184, 174, 164, 0.45)' : undefined
            }
          }}
        />
        {children}
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
